import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

const BORDER = {
    bottom: { style: 'thin' },
    top: { style: 'thin' },
    left: { style: 'thin' },
    right: { style: 'thin' }
};

const COLORS = {
    green: 'FF92D050',
    yellow: 'FFFFFF00',
    lightYellow: 'FFFFF2CC'
};

const DATE_TIME_FORMAT = 'YYYY/MM/DD HH:mm:ss';
const HEADER_ROW = 1;
const DATA_START_ROW = 2;

const normalize = value => `${value || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');

const columnNumberToLetter = (colNum) => {
     let letter = '';
     while (colNum > 0) {
         colNum--;
         letter = String.fromCharCode(65 + (colNum % 26)) + letter;
         colNum = Math.floor(colNum / 26);
     }
     return letter;
};

const columns = [
    { header: 'Nomor', rowNumber: true, width: 10 },
    { header: 'Nomor HAWB', candidates: ['nomorhawb', 'hawbnumber', 'nohawb', 'hawb'], width: 18 },
    { header: 'Tanggal HAWB', candidates: ['tanggalhawb', 'hawbdate', 'tglhawb'], type: 'date', format: 'YYYY/MM/DD', width: 18 },
    { header: 'Jenis Dokumen', candidates: ['jenisdokumen', 'documenttype', 'dokumen', 'kodedokumen'], width: 20 },
    { header: 'WaktuBongkar', candidates: ['waktubongkar', 'bongkartime', 'dischargetime'], type: 'dateTime', width: 24 },
    { header: 'WaktuGateIn', candidates: ['waktugatein', 'gateintime'], type: 'dateTime', width: 24 },
    { header: 'WaktuAjuDokumen', candidates: ['waktuajudokumen', 'ajudocumenttime', 'waktuaju'], type: 'dateTime', width: 24 },
    { header: 'WaktuSPPB', candidates: ['waktusppb', 'sppbtime', 'tglsppb'], type: 'dateTime', width: 24 },
    { header: 'WaktuGateOut', candidates: ['waktugateout', 'gateouttime'], type: 'dateTime', width: 24 },
    { header: 'JumlahDischargeProcess', candidates: ['jumlahdischargeprocess', 'dischargeprocess'], type: 'duration', durationColumns: [4, 5], width: 24 },
    { header: 'JumlahCustomsClearance', candidates: ['jumlahcustomsclearance', 'customsclearance'], type: 'duration', durationColumns: [6, 7], width: 24 },
    { header: 'JumlahGateOutProcess', candidates: ['jumlahgateoutprocess', 'gateoutprocess'], type: 'duration', durationColumns: [7, 8], width: 24 },
    { header: 'TotalImportClearanceTime', candidates: ['totalimportclearancetime', 'totalexportclearancetime', 'totalclearancetime'], type: 'duration', durationColumns: [4, 8], width: 28 }
];

const getCellStyle = ({ bold = false, fill, align = { horizontal: 'center', vertical: 'middle' }, numFmt } = {}) => {
    const style = {
        alignment: { wrapText: true, ...align },
        border: BORDER
    };

    if (bold) style.font = { bold: true };
    if (fill) style.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    if (numFmt) style.numFmt = numFmt;

    return style;
}

const applyCell = (cell, value = '', options = {}) => {
    cell.value = value;
    Object.assign(cell, { style: getCellStyle(options) });
}

const mergeAndFill = (sheet, row, startColumn, endColumn, value, options = {}) => {
     if (startColumn !== endColumn) {
         const startLetter = columnNumberToLetter(startColumn);
         const endLetter = columnNumberToLetter(endColumn);
         sheet.mergeCells(`${startLetter}${row}:${endLetter}${row}`);
     }
 
     for (let column = startColumn; column <= endColumn; column++) {
         Object.assign(sheet.getCell(row, column), { style: getCellStyle(options) });
     }
 
     sheet.getCell(row, startColumn).value = value;
}

const getFieldValue = (row, field) => {
    if (!field || !row) return undefined;

    if (field.field) {
        return row[field.name] && row[field.name][field.field] !== undefined && row[field.name][field.field] !== null
            ? row[field.name][field.field]
            : undefined;
    }

    return row[field.name] !== undefined && row[field.name] !== null ? row[field.name] : undefined;
}

const getValueByCandidates = (row, candidates = []) => {
    if (!row) return undefined;

    const normalizedCandidates = candidates.map(normalize);
    const keys = Object.keys(row);
    const matchedKey = keys.find(key => normalizedCandidates.includes(normalize(key)));

    return matchedKey ? row[matchedKey] : undefined;
}

const resolveField = (header = [], fields = [], column, fallbackIndex) => {
    const candidates = [column.header, ...(column.candidates || [])].map(normalize);
    const headerIndex = header.findIndex(item => candidates.includes(normalize(item)));

    if (headerIndex >= 0 && fields[headerIndex]) return fields[headerIndex];

    const field = fields.find(item => candidates.includes(normalize(item.name)) || candidates.includes(normalize(item.field)));
    return field || fields[fallbackIndex];
}

const formatDateValue = (value, format) => {
    const date = parseDate(value);
    return date ? date.format(format) : value || '';
}

const parseDate = value => {
    if (!value || value === '-') return null;
    if (moment.isMoment(value)) return value.isValid() ? value : null;

    const date = moment(value, [
        moment.ISO_8601,
        'YYYYMMDDHHmmss',
        'YYYYMMDDHHmm',
        'YYYYMMDD',
        'YYYY/MM/DD HH:mm:ss',
        'YYYY-MM-DD HH:mm:ss',
        'YYYY/MM/DD',
        'YYYY-MM-DD'
    ], true);

    if (date.isValid()) return date;

    const fallbackDate = moment(value);
    return fallbackDate.isValid() ? fallbackDate : null;
}

const getRawColumnValue = (row, column, field) => {
    return getFieldValue(row, field) ?? getValueByCandidates(row, [column.header, ...(column.candidates || [])]);
}

const getDurationValue = (row, durationColumns, resolvedFields) => {
    if (!durationColumns || durationColumns.length !== 2) return undefined;

    const [startColumn, endColumn] = durationColumns;
    const startValue = getRawColumnValue(row, columns[startColumn], resolvedFields[startColumn]);
    const endValue = getRawColumnValue(row, columns[endColumn], resolvedFields[endColumn]);
    const startDate = parseDate(startValue);
    const endDate = parseDate(endValue);

    if (!startDate || !endDate) return undefined;

    return Number((endDate.diff(startDate, 'seconds') / 86400).toFixed(2));
}

const formatColumnValue = (row, column, field, rowIndex, resolvedFields) => {
    if (column.rowNumber) return rowIndex + 1;

    const value = getRawColumnValue(row, column, field) ?? getDurationValue(row, column.durationColumns, resolvedFields);

    if (column.type === 'dateTime') return formatDateValue(value, DATE_TIME_FORMAT);
    if (column.type === 'date' || field?.type === 'time') return formatDateValue(value, column.format || field?.format || DATE_TIME_FORMAT);

    return value ?? '';
}

const getDataCellOptions = column => {
     if (column.rowNumber) return { align: { horizontal: 'right', vertical: 'middle' } };
     if (column.type === 'duration') return { align: { horizontal: 'right', vertical: 'middle' }, numFmt: '#,##0.00' };
 
     return { align: { horizontal: 'center', vertical: 'middle' } };
}

const createHeader = (sheet) => {
     // Row 1: Column headers only
     columns.forEach((column, index) => {
         const colIndex = index + 1;
         applyCell(sheet.getCell(HEADER_ROW, colIndex), column.header, { bold: true });
     });
};

const createFooterInfo = (sheet, dataEndRow) => {
     const row1 = dataEndRow + 1;
     const row2 = dataEndRow + 2;
     const row3 = dataEndRow + 3;
     const row4 = dataEndRow + 4;
 
     // Helper to add border to all cells in a row
     const addBordersToRow = (row) => {
         for (let col = 1; col <= 13; col++) {
             const cell = sheet.getCell(row, col);
             cell.border = BORDER;
         }
     };
 
     // Row 1: DISCHARGE PROCESS (E-F), CUSTOMS CLEARANCE (G-H), and <hari> labels (J-M)
     mergeAndFill(sheet, row1, 5, 6, 'DISCHARGE PROCESS', { bold: true, fill: COLORS.green });
     mergeAndFill(sheet, row1, 7, 8, 'CUSTOMS CLEARANCE', { bold: true, fill: COLORS.yellow });
     ['J', 'K', 'L', 'M'].forEach(col => {
         applyCell(sheet.getCell(`${col}${row1}`), '<hari>', { border: BORDER });
     });
     addBordersToRow(row1);
 
     // Row 2: GATE OUT PROCESS (I only) and <hari> labels (J-M)
     mergeAndFill(sheet, row2, 9, 9, 'GATE OUT PROCESS', { bold: true, fill: COLORS.green });
     ['J', 'K', 'L', 'M'].forEach(col => {
         applyCell(sheet.getCell(`${col}${row2}`), '<hari>', { border: BORDER });
     });
     addBordersToRow(row2);
 
     // Row 3: IMPORT CLEARANCE TIME (E-I) and <hari> labels (J-M)
     mergeAndFill(sheet, row3, 5, 9, 'IMPORT CLEARANCE TIME', { bold: true, fill: COLORS.lightYellow });
     ['J', 'K', 'L', 'M'].forEach(col => {
         applyCell(sheet.getCell(`${col}${row3}`), '<hari>', { border: BORDER });
     });
     addBordersToRow(row3);
 
     // Row 4: Footer note (A-I) and <hari> labels (J-M)
     sheet.mergeCells(`A${row4}:I${row4}`);
     applyCell(
         sheet.getCell(`A${row4}`),
         'JUMLAH SELISIH WAKTU MULAI DARI WAKTU BONGKAR KE WAKTU GATE OUT TPS',
         { bold: true, border: BORDER }
     );
     ['J', 'K', 'L', 'M'].forEach(col => {
         applyCell(sheet.getCell(`${col}${row4}`), '<hari>', { border: BORDER });
     });
     addBordersToRow(row4);
};

export const exportCustomClearanceExcel = async (data, header, fields, repType, title, date) => {
     const workbook = new ExcelJS.Workbook();
     const worksheet = workbook.addWorksheet('Report', { properties: { tabColor: { argb: 'FF00FF00' } } });
     const reportData = Array.isArray(data) ? data : [];
     const reportHeader = Array.isArray(header) ? header : [];
     const reportFields = Array.isArray(fields) ? fields : [];
     const resolvedFields = columns.map((column, index) => column.rowNumber ? null : resolveField(reportHeader, reportFields, column, index - 1));
 
     workbook.title = title;
     workbook.subject = `${title} Periode ${date}`;
     workbook.creator = 'ESI - EDIFly Solusi Indonesia';
     workbook.created = new Date();
 
     worksheet.views = [{ state: 'frozen', ySplit: 1 }];
     columns.forEach((column, index) => {
         worksheet.getColumn(index + 1).width = column.width;
     });
 
     // Add header row
     createHeader(worksheet);
 
     // Add data rows starting from row 2
     reportData.forEach((row, rowIndex) => {
         columns.forEach((column, columnIndex) => {
             const value = formatColumnValue(row, column, resolvedFields[columnIndex], rowIndex, resolvedFields);
             applyCell(worksheet.getCell(rowIndex + DATA_START_ROW, columnIndex + 1), value, getDataCellOptions(column));
         });
     });
 
     // Add footer info after data
     const dataEndRow = reportData.length + DATA_START_ROW - 1;
     createFooterInfo(worksheet, dataEndRow);
 
     const buffer = await workbook.xlsx.writeBuffer();
     const fileDate = `${date || ''}`.split('/').join('_');
     const filename = fileDate ? `${title}_${fileDate}` : title;
 
     saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${filename}.xlsx`);
     return true;
}
